import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Send, Loader2, CheckCircle2, Clock, AlertTriangle,
  Calendar, Target, X,
} from 'lucide-react';
import { T, ROLES, springs } from '../../lib/tokens';
import { isLocalAvailable, apiFetch } from '../../lib/api';
import { Glass } from '../glass/Glass';

const fmtDur = (m) => m >= 60 ? `${Math.floor(m/60)}h${m%60 ? ` ${m%60}m` : ''}` : `${m}m`;

export const AICommandBar = ({ open, onClose, onUpdate, triggerScheduler }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agenda, setAgenda] = useState(null);
  const inputRef = useRef(null);
  const messagesRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
      // Load agenda on open
      loadAgenda();
    }
    if (!open) {
      setMessages([]);
      setInput('');
      setAgenda(null);
    }
  }, [open]);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  const loadAgenda = async () => {
    if (!isLocalAvailable()) return;
    try {
      const res = await apiFetch('/ai/agenda');
      if (res.ok) {
        const data = await res.json();
        setAgenda(data);
      }
    } catch {}
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages(prev => [...prev, { role: 'user', text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await apiFetch('/ai/create-task', {
        method: 'POST',
        body: JSON.stringify({ instruction: text }),
      });
      const data = await res.json();

      if (res.ok && data.task) {
        const p = data.parsed;
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: `Created: **${data.task.title}**`,
          task: data.task,
          parsed: p,
        }]);
        if (onUpdate) onUpdate();
        if (triggerScheduler) triggerScheduler();
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: `Sorry, I couldn't create that task: ${data.error || 'Unknown error'}`,
          error: true,
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: `Error: ${err.message}`,
        error: true,
      }]);
    }

    setLoading(false);
  };

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }} transition={springs.gentle}
        onClick={e => e.stopPropagation()}
        style={{
          width: 520, maxHeight: '75vh', display: 'flex', flexDirection: 'column',
          background: '#fff', borderRadius: 16, boxShadow: T.shadowLg, overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: 'linear-gradient(135deg, #D4921E, #C86878)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles size={14} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>AI Assistant</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>Create tasks with natural language</div>
          </div>
          <button onClick={onClose} style={{ padding: 4, border: 'none', background: 'none', cursor: 'pointer', color: T.textLight }}>
            <X size={16} />
          </button>
        </div>

        {/* Agenda card */}
        {agenda && (
          <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(0,0,0,0.04)', background: 'rgba(0,0,0,0.01)' }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {agenda.currentTask && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.green, animation: 'pulse 2s infinite' }} />
                  <span style={{ fontSize: 11, color: T.green, fontWeight: 600 }}>Now:</span>
                  <span style={{ fontSize: 11, color: T.text }}>{agenda.currentTask.title}</span>
                  <span style={{ fontSize: 10, color: T.textMuted }}>until {agenda.currentTask.until}</span>
                </div>
              )}
              {!agenda.currentTask && agenda.nextTask && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Clock size={11} color={T.blue} />
                  <span style={{ fontSize: 11, color: T.blue, fontWeight: 600 }}>Next:</span>
                  <span style={{ fontSize: 11, color: T.text }}>{agenda.nextTask.title}</span>
                  <span style={{ fontSize: 10, color: T.textMuted }}>at {agenda.nextTask.at}</span>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: T.textMuted }}>{agenda.summary?.scheduledToday || 0} scheduled today</span>
                {agenda.summary?.overdue > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 600, color: T.red }}>{agenda.summary.overdue} overdue</span>
                )}
                {agenda.summary?.blocked > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 600, color: T.violet }}>{agenda.summary.blocked} blocked</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        <div ref={messagesRef} style={{
          flex: 1, overflowY: 'auto', padding: '16px 20px',
          display: 'flex', flexDirection: 'column', gap: 12,
          minHeight: 120, maxHeight: 'calc(75vh - 180px)',
        }}>
          {messages.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: T.textMuted }}>
              <div style={{ fontSize: 13 }}>Type a task in natural language</div>
              <div style={{ fontSize: 11, marginTop: 6, lineHeight: 1.6, color: T.textLight }}>
                "Follow up with Leo tomorrow at 10am"<br />
                "Draft Liken investor update by Friday, 2 hours"<br />
                "Review Oneday curriculum, low priority"
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '85%', padding: '8px 14px', borderRadius: 12,
                background: msg.role === 'user' ? T.amber : msg.error ? T.redLight : 'rgba(0,0,0,0.04)',
                color: msg.role === 'user' ? '#fff' : T.text,
                fontSize: 13, lineHeight: 1.4,
              }}>
                <div>{msg.text?.replace(/\*\*(.*?)\*\*/g, '$1')}</div>
                {msg.task && (
                  <div style={{
                    marginTop: 8, padding: '8px 10px', borderRadius: 8,
                    background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(0,0,0,0.06)',
                  }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <CheckCircle2 size={12} color={T.green} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{msg.task.title}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      {msg.parsed?.workspace && (
                        <span style={{ fontSize: 10, fontWeight: 600, color: ROLES[msg.parsed.workspace]?.color || T.textMuted, padding: '1px 5px', borderRadius: 3, background: ROLES[msg.parsed.workspace]?.light || 'rgba(0,0,0,0.04)' }}>
                          {ROLES[msg.parsed.workspace]?.label || msg.parsed.workspace}
                        </span>
                      )}
                      <span style={{ fontSize: 10, color: T.textMuted }}>
                        <Clock size={9} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                        {fmtDur(msg.task.duration_minutes || 30)}
                      </span>
                      {msg.task.deadline && (
                        <span style={{ fontSize: 10, color: T.textMuted }}>
                          <Calendar size={9} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                          {new Date(msg.task.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                      <span style={{ fontSize: 10, color: T.textMuted }}>P{msg.task.priority || 2}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0' }}>
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                <Loader2 size={14} color={T.textMuted} />
              </motion.div>
              <span style={{ fontSize: 12, color: T.textMuted }}>Creating task...</span>
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 16px', borderTop: '1px solid rgba(0,0,0,0.06)',
        }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Describe a task..."
            disabled={loading}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 8,
              border: '1px solid rgba(0,0,0,0.08)', fontSize: 13,
              fontFamily: 'inherit', outline: 'none', color: T.text,
            }}
          />
          <button onClick={send} disabled={!input.trim() || loading} style={{
            width: 34, height: 34, borderRadius: 8, border: 'none',
            background: input.trim() ? T.amber : 'rgba(0,0,0,0.04)',
            color: input.trim() ? '#fff' : T.textLight,
            cursor: input.trim() ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Send size={14} />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
